import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from 'material-ui/FlatButton';
import TextField from 'material-ui/TextField';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import Checkbox from 'material-ui/Checkbox';
import LinearProgress from 'material-ui/LinearProgress';
import {
    Table,
    TableBody,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn,
} from 'material-ui/Table';
import {
    createCorpus,
    getCorpus,
    listCoverageMetrics,
    updateCorpus,
    updateCorpusMail,
    removeCorpusMail,
} from 'services/testRuns';
import AddMailsDialog from './AddMailsDialog';
import ProposeCompositionDialog from './ProposeCompositionDialog';
import './styles.scss';

function scoreLabel(metrics, id) {
    const found = (Array.isArray(metrics) ? metrics : []).find(metric => metric.id === id);
    return found ? found.label : id;
}

function formatScore(item) {
    if (!item || item.percent === null || item.percent === undefined) {
        return '—';
    }
    return `${item.percent}% (${item.covered}/${item.total})`;
}

function CoverageScores({ scores, metrics, fill }) {
    const full = scores && Array.isArray(scores.items) ? scores.items : [];
    const reduced = scores && Array.isArray(scores.reduced) ? scores.reduced : null;
    if (full.length === 0) {
        return (
            <p className="test-runs-hint">
                Coverage has not been computed yet. Save the corpus to compute it.
            </p>
        );
    }
    const singleType = fill && fill.annotationTypes && fill.annotationTypes.length === 1
        ? fill.annotationTypes[0]
        : '';
    return (
        <div className="bbr-coverage">
            <table className="bbr-score-table">
                <thead>
                    <tr>
                        <th>Score</th>
                        <th>Full set</th>
                        {reduced ? <th>Reduced set</th> : null}
                    </tr>
                </thead>
                <tbody>
                    {full.map(item => {
                        const scoped = reduced && reduced.find(row => row.metric === item.metric);
                        return (
                            <tr key={item.metric}>
                                <td>{scoreLabel(metrics, item.metric)}</td>
                                <td>{formatScore(item)}</td>
                                {reduced ? <td>{formatScore(scoped)}</td> : null}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div className="test-runs-hint">
                {reduced
                    ? `Reduced set uses the stored proposal options${singleType ? ` (${singleType} documents)` : ''}. `
                    : 'Full set counts every feature. '}
                Computed {formatDate(scores.computedAt)}. Save again to refresh.
            </div>
        </div>
    );
}

function formatDate(value) {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function optionValues(corpus) {
    const options = (corpus && corpus.options) || {};
    return {
        replicas: options.replicas === undefined ? '' : String(options.replicas),
        timeout: options.timeout === undefined ? '' : String(options.timeout),
        memoryReservationGb: options.memoryReservationGb === undefined ? '' : String(options.memoryReservationGb),
        japeLimitsEnabled: Boolean(options.japeLimitsEnabled),
        japeTrackAnnotations: options.japeTrackAnnotations === undefined ? true : Boolean(options.japeTrackAnnotations),
        skipFailedCheck: Boolean(options.skipFailedCheck),
    };
}

export default function CorpusEditorPage({ params, router }) {
    const isNew = !params.id || params.id === 'new';
    const queryClient = useQueryClient();
    const [name, setName] = useState('');
    const [replicas, setReplicas] = useState('');
    const [timeout, setTimeoutSec] = useState('');
    const [memoryReservationGb, setMemoryReservationGb] = useState('');
    const [japeLimitsEnabled, setJapeLimitsEnabled] = useState(false);
    const [japeTrackAnnotations, setJapeTrackAnnotations] = useState(true);
    const [skipFailedCheck, setSkipFailedCheck] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [proposeOpen, setProposeOpen] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [dateDrafts, setDateDrafts] = useState({});

    const corpusQuery = useQuery(
        ['bbr-corpus', params.id],
        () => getCorpus(params.id),
        { enabled: !isNew },
    );
    const metricsQuery = useQuery('bbr-coverage-metrics', listCoverageMetrics, { enabled: !isNew });

    useEffect(() => {
        if (isNew) {
            setName('');
            setReplicas('');
            setTimeoutSec('');
            setMemoryReservationGb('');
            setJapeLimitsEnabled(false);
            setJapeTrackAnnotations(true);
            setSkipFailedCheck(false);
            return;
        }
        if (corpusQuery.data && corpusQuery.data.name) {
            setName(corpusQuery.data.name);
            const options = optionValues(corpusQuery.data);
            setReplicas(options.replicas);
            setTimeoutSec(options.timeout);
            setMemoryReservationGb(options.memoryReservationGb);
            setJapeLimitsEnabled(options.japeLimitsEnabled);
            setJapeTrackAnnotations(options.japeTrackAnnotations);
            setSkipFailedCheck(options.skipFailedCheck);
        }
    }, [isNew, corpusQuery.data]);

    const saveCorpus = useMutation(
        async () => {
            const trimmed = name.trim();
            if (!trimmed) {
                throw new Error('Name is required');
            }
            const body = { name: trimmed };
            if (replicas !== '') {
                body.replicas = Number(replicas);
            }
            if (timeout !== '') {
                body.timeout = Number(timeout);
            }
            if (memoryReservationGb !== '') {
                body.memoryReservationGb = Number(memoryReservationGb);
            }
            body.japeLimitsEnabled = japeLimitsEnabled;
            body.japeTrackAnnotations = japeTrackAnnotations;
            body.skipFailedCheck = skipFailedCheck;
            if (isNew) {
                return createCorpus(body);
            }
            return updateCorpus(params.id, body);
        },
        {
            onSuccess: (data) => {
                setSaveError('');
                queryClient.invalidateQueries('bbr-corpora');
                if (isNew) {
                    router.replace(`/bbr/corporas/${data._id}`);
                    return;
                }
                queryClient.invalidateQueries(['bbr-corpus', params.id]);
            },
            onError: (err) => setSaveError(err.message),
        },
    );

    const removeMail = useMutation(
        docId => removeCorpusMail(params.id, docId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['bbr-corpus', params.id]);
                queryClient.invalidateQueries('bbr-corpora');
            },
        },
    );

    const saveMail = useMutation(
        ({ docId, date, annotationType }) => updateCorpusMail(params.id, docId, { date, annotationType }),
        {
            onSuccess: (_data, vars) => {
                setDateDrafts((current) => {
                    const next = { ...current };
                    delete next[vars.docId];
                    return next;
                });
                queryClient.invalidateQueries(['bbr-corpus', params.id]);
            },
        },
    );

    if (!isNew && corpusQuery.error) {
        return <div className="test-runs-page test-runs-error">{corpusQuery.error.message}</div>;
    }
    if (!isNew && (corpusQuery.isLoading || !corpusQuery.data)) {
        return <LinearProgress />;
    }

    const corpus = corpusQuery.data || { mails: [] };
    const mails = corpus.mails || [];
    const saved = optionValues(corpus);
    const savedName = isNew ? '' : (corpus.name || '');
    const dirty = name.trim() !== savedName
        || replicas !== saved.replicas
        || timeout !== saved.timeout
        || memoryReservationGb !== saved.memoryReservationGb
        || japeLimitsEnabled !== saved.japeLimitsEnabled
        || japeTrackAnnotations !== saved.japeTrackAnnotations
        || skipFailedCheck !== saved.skipFailedCheck;
    const existingSourceIds = mails.map(mail => mail.sourceMailId).filter(Boolean);

    return (
        <div className="test-runs-page">
            <div className="test-runs-toolbar">
                <h2>
                    <Link to="/bbr/corporas">Corporas</Link>
                    {' / '}
                    {isNew ? 'New' : (corpus.name || params.id)}
                </h2>
            </div>
            {saveError ? <div className="test-runs-error">{saveError}</div> : null}
            {removeMail.error ? <div className="test-runs-error">{removeMail.error.message}</div> : null}
            {saveMail.error ? <div className="test-runs-error">{saveMail.error.message}</div> : null}
            <div className="bbr-corpus-name">
                <TextField
                    floatingLabelText="Name"
                    value={name}
                    onChange={(e, value) => setName(value)}
                    fullWidth
                />
                <RaisedButton
                    label={isNew ? 'Create' : 'Save'}
                    primary
                    disabled={!name.trim() || !dirty || saveCorpus.isLoading}
                    onClick={() => saveCorpus.mutate()}
                    style={{ marginLeft: 12, flexShrink: 0 }}
                />
            </div>
            <div className="bbr-corpus-options">
                <TextField
                    floatingLabelText="Replicas (optional)"
                    value={replicas}
                    onChange={(e, value) => setReplicas(value)}
                    style={{ marginRight: 16, width: 160 }}
                />
                <TextField
                    floatingLabelText="Per-doc timeout sec (optional)"
                    value={timeout}
                    onChange={(e, value) => setTimeoutSec(value)}
                    style={{ marginRight: 16, width: 220 }}
                />
                <TextField
                    floatingLabelText="Memory reservation GB (optional, default 6)"
                    value={memoryReservationGb}
                    onChange={(e, value) => setMemoryReservationGb(value)}
                    style={{ marginRight: 16, width: 280 }}
                />
                <Checkbox
                    label="japeLimitsEnabled"
                    checked={japeLimitsEnabled}
                    onCheck={(e, checked) => setJapeLimitsEnabled(checked)}
                    style={{ width: 220, display: 'inline-block' }}
                />
                <Checkbox
                    label="japeTrackAnnotations"
                    checked={japeTrackAnnotations}
                    onCheck={(e, checked) => setJapeTrackAnnotations(checked)}
                    style={{ width: 220, display: 'inline-block' }}
                />
                <Checkbox
                    label="skipFailedCheck"
                    checked={skipFailedCheck}
                    onCheck={(e, checked) => setSkipFailedCheck(checked)}
                    style={{ width: 220, display: 'inline-block' }}
                />
            </div>
            {!isNew ? (
                <CoverageScores
                    scores={corpus.scores}
                    metrics={metricsQuery.data}
                    fill={corpus.fill}
                />
            ) : null}
            <div className="test-runs-toolbar">
                <h3>
                    Documents ({mails.length}
                    {mails.length ? ` · ${mails.filter(mail => mail.annotationType === 'cargo').length} cargo · ${mails.filter(mail => mail.annotationType === 'vessel').length} vessel` : ''})
                </h3>
                <span>
                    <RaisedButton
                        label="Propose changes"
                        disabled={isNew}
                        onClick={() => setProposeOpen(true)}
                        style={{ marginRight: 12 }}
                    />
                    <RaisedButton
                        label="Add documents"
                        primary
                        disabled={isNew}
                        onClick={() => setAddOpen(true)}
                    />
                </span>
            </div>
            {isNew ? (
                <p className="test-runs-hint">
                    Create the corpus first. Documents stay in the order they are added.
                </p>
            ) : (
                <Table selectable={false}>
                    <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                        <TableRow>
                            <TableHeaderColumn style={{ width: 48 }}>#</TableHeaderColumn>
                            <TableHeaderColumn>Subject</TableHeaderColumn>
                            <TableHeaderColumn>Type</TableHeaderColumn>
                            <TableHeaderColumn>Letter</TableHeaderColumn>
                            <TableHeaderColumn>Received</TableHeaderColumn>
                            <TableHeaderColumn>Date</TableHeaderColumn>
                            <TableHeaderColumn>Added</TableHeaderColumn>
                            <TableHeaderColumn style={{ width: 96 }} />
                        </TableRow>
                    </TableHeader>
                    <TableBody displayRowCheckbox={false}>
                        {mails.map((mail, index) => {
                            const draft = dateDrafts[mail.id];
                            const dateValue = draft === undefined ? (mail.date || '') : draft;
                            const dateDirty = dateValue !== (mail.date || '');
                            return (
                                <TableRow key={mail.id}>
                                    <TableRowColumn style={{ width: 48 }}>{index + 1}</TableRowColumn>
                                    <TableRowColumn>{mail.subject || '—'}</TableRowColumn>
                                    <TableRowColumn>
                                        <SelectField
                                            value={mail.annotationType}
                                            onChange={(e, i, value) => saveMail.mutate({ docId: mail.id, annotationType: value })}
                                            disabled={saveMail.isLoading}
                                            style={{ width: 120 }}
                                        >
                                            <MenuItem value="cargo" primaryText="cargo" />
                                            <MenuItem value="vessel" primaryText="vessel" />
                                        </SelectField>
                                    </TableRowColumn>
                                    <TableRowColumn>
                                        {mail.sourceMailId ? (
                                            <a href={`/${mail.sourceMailId}`} target="_blank" rel="noreferrer">
                                                {mail.sourceMailId}
                                            </a>
                                        ) : (
                                            mail.id.slice(0, 8)
                                        )}
                                    </TableRowColumn>
                                    <TableRowColumn>{formatDate(mail.receivedAt)}</TableRowColumn>
                                    <TableRowColumn>
                                        <TextField
                                            name={`date-${mail.id}`}
                                            value={dateValue}
                                            hintText="dd-MM-yyyy"
                                            onChange={(e, value) => setDateDrafts(current => ({ ...current, [mail.id]: value }))}
                                            style={{ width: 120 }}
                                        />
                                        <FlatButton
                                            label="Save"
                                            primary
                                            disabled={!dateDirty || saveMail.isLoading}
                                            onClick={() => saveMail.mutate({ docId: mail.id, date: dateValue })}
                                        />
                                    </TableRowColumn>
                                    <TableRowColumn>{formatDate(mail.addedAt)}</TableRowColumn>
                                    <TableRowColumn style={{ width: 96 }}>
                                        <FlatButton
                                            label="Remove"
                                            secondary
                                            disabled={removeMail.isLoading}
                                            onClick={() => {
                                                if (window.confirm('Unlink this document from the corpus?')) {
                                                    removeMail.mutate(mail.id);
                                                }
                                            }}
                                        />
                                    </TableRowColumn>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
            {!isNew && mails.length === 0 && (
                <p className="test-runs-hint">No documents yet. Add ids or mail links to fill this corpus.</p>
            )}
            {!isNew && (
                <ProposeCompositionDialog
                    open={proposeOpen}
                    corpusId={params.id}
                    fill={corpus.fill}
                    onClose={() => setProposeOpen(false)}
                    onApplied={() => {
                        queryClient.invalidateQueries(['bbr-corpus', params.id]);
                        queryClient.invalidateQueries('bbr-corpora');
                    }}
                    onCreated={(created) => {
                        queryClient.invalidateQueries('bbr-corpora');
                        if (created && created._id) {
                            router.push(`/bbr/corporas/${created._id}`);
                        }
                    }}
                />
            )}
            {!isNew && (
                <AddMailsDialog
                    open={addOpen}
                    corpusId={params.id}
                    existingSourceIds={existingSourceIds}
                    onClose={() => setAddOpen(false)}
                    onAdded={() => {
                        queryClient.invalidateQueries(['bbr-corpus', params.id]);
                        queryClient.invalidateQueries('bbr-corpora');
                    }}
                />
            )}
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/CorpusEditorPage.jsx